import { create } from "zustand";
import toast from "react-hot-toast";
import { axiosInstance } from "../lib/axios";
import { useAuthStore } from "./useAuthStore";

const getInitialGameState = (gameName, hostId) => {
  switch (gameName) {
    case 'tictactoe':
      return { board: Array(9).fill(null), turn: hostId, scores: { host: 0, guest: 0, draws: 0 } };
    case 'truthordare':
      return { currentTurn: hostId, stage: 'choosing', selection: null };
    case 'trivia':
      return { currentQuestionIndex: 0, playerAnswers: {}, revealAnswers: false };
    case 'wouldyourather':
      return { currentQuestionIndex: 0, playerAnswers: {}, category: null, revealAnswers: false };
    case 'codraw':
      return { canvasCleared: false };
    default:
      return {};
  }
};

export const useChatStore = create((set, get) => ({
  messages: [],
  messagesCache: {},
  users: [],
  selectedUser: null,
  isUsersLoading: false,
  isMessagesLoading: false,
  isTyping: false,
  replyingTo: null,

  setReplyingTo: (message) => set({ replyingTo: message }),
  clearReplyingTo: () => set({ replyingTo: null }),

  getUsers: async () => {
    // Only set loading on initial fetch if empty
    if (get().users.length === 0) {
      set({ isUsersLoading: true });
    }
    try {
      const res = await axiosInstance.get("/messages/users");
      set({ users: res.data });
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load sidebar users");
    } finally {
      set({ isUsersLoading: false });
    }
  },

  getMessages: async (userId) => {
    const cached = get().messagesCache[userId];
    if (!cached || cached.length === 0) {
      set({ isMessagesLoading: true });
    }
    try {
      const res = await axiosInstance.get(`/messages/${userId}`);
      const messageList = Array.isArray(res.data) ? res.data : (res.data?.messages || []);
      set((state) => ({
        messages: messageList,
        messagesCache: { ...state.messagesCache, [userId]: messageList },
      }));
      get().markMessagesAsRead(userId).catch(() => {});
    } catch (error) {
      console.error("Error in getMessages:", error);
      if (!cached) set({ messages: [] });
    } finally {
      set({ isMessagesLoading: false });
    }
  },

  setSelectedUser: (selectedUser) => {
    set({ selectedUser });
    if (selectedUser) {
      const uId = selectedUser._id || selectedUser.id;
      const cached = get().messagesCache[uId];
      if (cached && cached.length > 0) {
        set({ messages: cached, isMessagesLoading: false });
      }
    }
  },
  sendMessage: async (messageData) => {
    const { selectedUser, messages } = get();
    const authUser = useAuthStore.getState().authUser;
    if (!selectedUser || !authUser) return;

    const uId = selectedUser._id || selectedUser.id;
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const optimisticMessage = {
      _id: tempId,
      senderId: authUser._id || authUser.id,
      receiverId: uId,
      text: messageData.text || "",
      image: messageData.image || null,
      audio: messageData.audio || null,
      replyTo: messageData.replyTo || null,
      reactions: [],
      isEphemeral: messageData.isEphemeral || false,
      isDisappearing: messageData.isDisappearing || false,
      disappearingMode: messageData.disappearingMode || "none",
      peekDuration: messageData.peekDuration || 10,
      isExpired: false,
      openedAt: null,
      read: false,
      createdAt: new Date().toISOString(),
    };

    // 1. Instantly update sender screen (0ms)
    const currentMessages = Array.isArray(messages) ? messages : [];
    const updatedMessages = [...currentMessages, optimisticMessage];
    set((state) => ({
      messages: updatedMessages,
      messagesCache: { ...state.messagesCache, [uId]: updatedMessages },
      replyingTo: null,
    }));

    // 2. Transmit directly over active Socket.IO connection (<5ms delivery to recipient)
    // The socket server saves to DB — so we skip HTTP when socket is connected
    const socket = useAuthStore.getState().socket;
    if (socket && socket.connected) {
      socket.emit("sendMessage", {
        receiverId: uId,
        text: messageData.text || "",
        image: messageData.image || null,
        audio: messageData.audio || null,
        replyTo: messageData.replyTo || null,
        isEphemeral: messageData.isEphemeral || false,
        isDisappearing: messageData.isDisappearing || false,
        disappearingMode: messageData.disappearingMode || "none",
        peekDuration: messageData.peekDuration || 10,
        tempId,
      });
      // Socket server will emit 'messageDelivered' with the real DB id
      // No HTTP fallback needed — avoids duplicate DB save
      return;
    }

    // 3. Fallback HTTP when socket is NOT connected (ensures DB persistence)
    axiosInstance.post(`/messages/send/${uId}`, messageData)
      .then((res) => {
        set((state) => {
          // Fix #11: Use state.messagesCache[uId] instead of state.messages
          // to avoid corrupting the wrong chat if the user switched conversations
          const cacheForUser = Array.isArray(state.messagesCache[uId]) ? state.messagesCache[uId] : [];
          const updatedCache = cacheForUser.map((msg) =>
            msg._id === tempId ? res.data : msg
          );
          return {
            messages: state.selectedUser &&
              String(state.selectedUser._id || state.selectedUser.id) === uId
              ? updatedCache
              : state.messages,
            messagesCache: { ...state.messagesCache, [uId]: updatedCache },
          };
        });
      })
      .catch((err) => {
        console.error("HTTP send message fallback error:", err);
      });
  },

  sendReaction: (messageId, emoji) => {
    const { selectedUser } = get();
    const authUser = useAuthStore.getState().authUser;
    if (!selectedUser || !authUser) return;
    const recipientId = selectedUser._id || selectedUser.id;
    const userId = authUser._id || authUser.id;

    // Optimistic update
    const updateMsgReactions = (list) =>
      (Array.isArray(list) ? list : []).map((msg) => {
        if (msg._id !== messageId) return msg;
        const reactions = Array.isArray(msg.reactions) ? [...msg.reactions] : [];
        const existingIndex = reactions.findIndex((r) => r.userId === userId);
        if (existingIndex > -1) {
          if (reactions[existingIndex].emoji === emoji) {
            reactions.splice(existingIndex, 1);
          } else {
            reactions[existingIndex].emoji = emoji;
          }
        } else {
          reactions.push({ userId, emoji });
        }
        return { ...msg, reactions };
      });

    set((state) => ({
      messages: updateMsgReactions(state.messages),
      messagesCache: {
        ...state.messagesCache,
        [recipientId]: updateMsgReactions(state.messagesCache[recipientId]),
      },
    }));

    const socket = useAuthStore.getState().socket;
    if (socket && socket.connected) {
      socket.emit("reactToMessage", { messageId, recipientId, emoji });
    } else {
      axiosInstance.post(`/messages/reaction/${messageId}`, { emoji }).catch(console.error);
    }
  },

  subscribeToMessages: () => {
    const { selectedUser } = get();
    if (!selectedUser) return;

    const socket = useAuthStore.getState().socket;

    // Prevent duplicate listeners
    socket.off("newMessage");
    socket.off("messageDelivered");

    socket.on("messageDelivered", ({ tempId, realId }) => {
      set((state) => ({
        messages: (Array.isArray(state.messages) ? state.messages : []).map((msg) =>
          msg._id === tempId ? { ...msg, _id: realId } : msg
        ),
      }));
    });

    socket.on("newMessage", async (newMessage) => {
      const { messages } = get();
      
      // Robust multi-factor deduplication
      const isDuplicate = messages.some((msg) => {
        if (msg._id === newMessage._id) return true;
        if (newMessage.tempId && msg._id === newMessage.tempId) return true;
        if (
          msg.senderId === newMessage.senderId &&
          msg.text && newMessage.text &&
          msg.text === newMessage.text &&
          Math.abs(new Date(msg.createdAt).getTime() - new Date(newMessage.createdAt).getTime()) < 3000
        ) {
          return true;
        }
        return false;
      });

      if (isDuplicate) return;

      const uId = String(selectedUser._id || selectedUser.id);
      const isMessageSentFromSelectedUser = String(newMessage.senderId).toLowerCase() === uId.toLowerCase();
      if (!isMessageSentFromSelectedUser) return;

      const updatedMessages = [...get().messages, newMessage];
      set((state) => ({
        messages: updatedMessages,
        messagesCache: { ...state.messagesCache, [uId]: updatedMessages },
      }));

      await get().markMessagesAsRead(uId);
    });

    // Fix #10: Use named handler references so unsubscribe only removes these specific
    // listeners, not all 'typing'/'stopTyping' handlers registered elsewhere globally
    const handleTyping = ({ senderId }) => {
      const uId = selectedUser ? String(selectedUser._id || selectedUser.id).toLowerCase() : null;
      if (uId && String(senderId).toLowerCase() === uId) {
        set({ isTyping: true });
      }
    };

    const handleStopTyping = ({ senderId }) => {
      const uId = selectedUser ? String(selectedUser._id || selectedUser.id).toLowerCase() : null;
      if (uId && String(senderId).toLowerCase() === uId) {
        set({ isTyping: false });
      }
    };

    // Store named handlers on the store state so unsubscribe can remove them
    set({ _typingHandler: handleTyping, _stopTypingHandler: handleStopTyping });

    socket.on("typing", handleTyping);
    socket.on("stopTyping", handleStopTyping);

    socket.on("messagesRead", ({ readerId }) => {
      const uId = selectedUser ? String(selectedUser._id || selectedUser.id).toLowerCase() : null;
      if (uId && String(readerId).toLowerCase() === uId) {
        set(state => ({
          messages: state.messages.map(msg => 
            String(msg.receiverId).toLowerCase() === uId ? { ...msg, read: true } : msg
          )
        }));
      }
    });

    socket.off("messageReactionUpdated");
    socket.on("messageReactionUpdated", ({ messageId, reactions }) => {
      set((state) => {
        const updateReactions = (list) =>
          (Array.isArray(list) ? list : []).map((msg) =>
            msg._id === messageId ? { ...msg, reactions } : msg
          );

        const currentSelectedId = state.selectedUser?._id || state.selectedUser?.id;
        return {
          messages: updateReactions(state.messages),
          messagesCache: currentSelectedId
            ? {
                ...state.messagesCache,
                [currentSelectedId]: updateReactions(state.messagesCache[currentSelectedId]),
              }
            : state.messagesCache,
        };
      });
    });

    socket.off("disappearingPhotoOpened");
    socket.on("disappearingPhotoOpened", ({ messageId, openedAt }) => {
      set((state) => {
        const updateMsg = (list) =>
          (Array.isArray(list) ? list : []).map((msg) =>
            msg._id === messageId ? { ...msg, openedAt } : msg
          );
        const currentSelectedId = state.selectedUser?._id || state.selectedUser?.id;
        return {
          messages: updateMsg(state.messages),
          messagesCache: currentSelectedId
            ? {
                ...state.messagesCache,
                [currentSelectedId]: updateMsg(state.messagesCache[currentSelectedId]),
              }
            : state.messagesCache,
        };
      });
    });

    socket.off("disappearingPhotoExpired");
    socket.on("disappearingPhotoExpired", ({ messageId }) => {
      set((state) => {
        const updateMsg = (list) =>
          (Array.isArray(list) ? list : []).map((msg) =>
            msg._id === messageId ? { ...msg, isExpired: true, image: null } : msg
          );
        const currentSelectedId = state.selectedUser?._id || state.selectedUser?.id;
        return {
          messages: updateMsg(state.messages),
          messagesCache: currentSelectedId
            ? {
                ...state.messagesCache,
                [currentSelectedId]: updateMsg(state.messagesCache[currentSelectedId]),
              }
            : state.messagesCache,
        };
      });
    });
  },

  openDisappearingPhoto: async (messageId, senderId) => {
    const socket = useAuthStore.getState().socket;
    if (socket && socket.connected) {
      socket.emit("openDisappearingPhoto", { messageId, senderId });
    }
    axiosInstance.post(`/messages/open-disappearing/${messageId}`).catch(() => {});
    set((state) => {
      const updateMsg = (list) =>
        (Array.isArray(list) ? list : []).map((msg) =>
          msg._id === messageId ? { ...msg, openedAt: new Date().toISOString() } : msg
        );
      const currentSelectedId = state.selectedUser?._id || state.selectedUser?.id;
      return {
        messages: updateMsg(state.messages),
        messagesCache: currentSelectedId
          ? {
              ...state.messagesCache,
              [currentSelectedId]: updateMsg(state.messagesCache[currentSelectedId]),
            }
          : state.messagesCache,
      };
    });
  },

  expireDisappearingPhoto: async (messageId, senderId) => {
    const socket = useAuthStore.getState().socket;
    if (socket && socket.connected) {
      socket.emit("expireDisappearingPhoto", { messageId, senderId });
    }
    axiosInstance.post(`/messages/expire-disappearing/${messageId}`).catch(() => {});
    set((state) => {
      const updateMsg = (list) =>
        (Array.isArray(list) ? list : []).map((msg) =>
          msg._id === messageId ? { ...msg, isExpired: true, image: null } : msg
        );
      const currentSelectedId = state.selectedUser?._id || state.selectedUser?.id;
      return {
        messages: updateMsg(state.messages),
        messagesCache: currentSelectedId
          ? {
              ...state.messagesCache,
              [currentSelectedId]: updateMsg(state.messagesCache[currentSelectedId]),
            }
          : state.messagesCache,
      };
    });
  },

  unsubscribeFromMessages: () => {
    const socket = useAuthStore.getState().socket;
    if (!socket) return;
    const { _typingHandler, _stopTypingHandler } = get();
    socket.off("newMessage");
    socket.off("messageDelivered");
    socket.off("messageReactionUpdated");
    socket.off("disappearingPhotoOpened");
    socket.off("disappearingPhotoExpired");
    // Fix #10: Remove only the specific named handlers, not all global typing listeners
    if (_typingHandler) socket.off("typing", _typingHandler);
    if (_stopTypingHandler) socket.off("stopTyping", _stopTypingHandler);
    socket.off("messagesRead");
    set({ _typingHandler: null, _stopTypingHandler: null });
  },

  getUnreadMessages: async () => {
    try {
      const res = await axiosInstance.get("/messages/unread");
      return res.data;
    } catch (error) {
      console.error("Error fetching unread messages:", error);
      return {};
    }
  },

  markMessagesAsRead: async (senderId) => {
    try {
      await axiosInstance.put(`/messages/read/${senderId}`);
      
      const socket = useAuthStore.getState().socket;
      if (socket) {
        socket.emit("markMessagesRead", { senderId });
      }
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  },

  sendTyping: () => {
    const { selectedUser } = get();
    const socket = useAuthStore.getState().socket;
    if (selectedUser && socket) {
      socket.emit("typing", { recipientId: selectedUser._id });
    }
  },

  sendStopTyping: () => {
    const { selectedUser } = get();
    const socket = useAuthStore.getState().socket;
    if (selectedUser && socket) {
      socket.emit("stopTyping", { recipientId: selectedUser._id });
    }
  },

  // --- Watch Party State & Methods ---
  watchParty: { active: false, url: '', senderId: null, isConnected: false },
  watchPartyActions: null, // to trigger actions on the player
  
  setWatchPartyActions: (actions) => set({ watchPartyActions: actions }),

  startWatchParty: (url) => {
    const { selectedUser } = get();
    if (!selectedUser) return;
    
    set({ watchParty: { active: true, url, senderId: useAuthStore.getState().authUser._id, isConnected: true } });
    
    const socket = useAuthStore.getState().socket;
    if (socket) {
      socket.emit("startWatchParty", { recipientId: selectedUser._id, videoUrl: url });
      toast.success("Watch party invitation sent!");
    }
  },

  changeWatchPartyVideo: (url) => {
    const { selectedUser } = get();
    if (!selectedUser) return;
    
    set({ watchParty: { ...get().watchParty, url } });
    
    const socket = useAuthStore.getState().socket;
    if (socket) {
      socket.emit("changeWatchPartyVideo", { recipientId: selectedUser._id, videoUrl: url });
      toast.success("Video changed!");
    }
  },

  joinWatchParty: (senderId, url) => {
    set({ watchParty: { active: true, url, senderId, isConnected: true } });
    const socket = useAuthStore.getState().socket;
    if (socket) {
      socket.emit("joinWatchParty", { recipientId: senderId });
    }
  },

  endWatchParty: () => {
    const { selectedUser } = get();
    set({ watchParty: { active: false, url: '', senderId: null, isConnected: false } });
    
    const socket = useAuthStore.getState().socket;
    if (socket && selectedUser) {
      socket.emit("endWatchParty", { recipientId: selectedUser._id });
    }
  },

  emitVideoPlay: (time) => {
    const { selectedUser } = get();
    const socket = useAuthStore.getState().socket;
    if (socket && selectedUser) {
      socket.emit("videoPlay", { recipientId: selectedUser._id, time });
    }
  },

  emitVideoPause: (time) => {
    const { selectedUser } = get();
    const socket = useAuthStore.getState().socket;
    if (socket && selectedUser) {
      socket.emit("videoPause", { recipientId: selectedUser._id, time });
    }
  },

  emitVideoSeek: (time) => {
    const { selectedUser } = get();
    const socket = useAuthStore.getState().socket;
    if (socket && selectedUser) {
      socket.emit("videoSeek", { recipientId: selectedUser._id, time });
    }
  },

  subscribeToWatchParty: () => {
    const socket = useAuthStore.getState().socket;
    if (!socket) return;

    // Note: watchPartyStarted is handled globally in useAuthStore.js

    socket.on("watchPartyJoined", ({ senderId }) => {
      const { selectedUser } = get();
      if (selectedUser && selectedUser._id === senderId) {
        set({ watchParty: { ...get().watchParty, isConnected: true } });
        toast.success("Partner joined the Watch Party!");
      }
    });

    socket.on("watchPartyVideoChanged", ({ senderId, videoUrl }) => {
      const { selectedUser } = get();
      if (selectedUser && selectedUser._id === senderId) {
        set({ watchParty: { ...get().watchParty, url: videoUrl } });
        toast.success("Watch Party Video Changed!");
      }
    });

    socket.on("watchPartyEnded", ({ senderId }) => {
      const { selectedUser } = get();
      if (selectedUser && selectedUser._id === senderId) {
        set({ watchParty: { active: false, url: '', senderId: null, isConnected: false } });
        toast.error("Partner left the Watch Party");
      }
    });

    socket.on("videoPlay", ({ senderId, time }) => {
      const { watchPartyActions } = get();
      if (watchPartyActions && watchPartyActions.onPlay) {
        watchPartyActions.onPlay(time);
      }
    });

    socket.on("videoPause", ({ senderId, time }) => {
      const { watchPartyActions } = get();
      if (watchPartyActions && watchPartyActions.onPause) {
        watchPartyActions.onPause(time);
      }
    });

    socket.on("videoSeek", ({ senderId, time }) => {
      const { watchPartyActions } = get();
      if (watchPartyActions && watchPartyActions.onSeek) {
        watchPartyActions.onSeek(time);
      }
    });
  },

  unsubscribeFromWatchParty: () => {
    const socket = useAuthStore.getState().socket;
    if (!socket) return;
    
    socket.off("watchPartyStarted");
    socket.off("watchPartyJoined");
    socket.off("watchPartyVideoChanged");
    socket.off("watchPartyEnded");
    socket.off("videoPlay");
    socket.off("videoPause");
    socket.off("videoSeek");
  },

  // --- Games State & Methods ---
  activeGame: null,
  receivedInvite: null,

  sendGameInvite: (gameName) => {
    const { selectedUser } = get();
    const socket = useAuthStore.getState().socket;
    const authUser = useAuthStore.getState().authUser;
    if (selectedUser && socket && authUser) {
      const recipientId = selectedUser._id || selectedUser.id;
      socket.emit("gameInvite", { to: recipientId, gameName, senderName: authUser.fullName });
      toast.success(`Sent invite for ${gameName}!`);
    }
  },

  acceptGameInvite: () => {
    const { receivedInvite, selectedUser } = get();
    const socket = useAuthStore.getState().socket;
    const authUser = useAuthStore.getState().authUser;
    if (receivedInvite && selectedUser && socket) {
      const recipientId = String(selectedUser._id || selectedUser.id);
      const myId = String(authUser._id || authUser.id);
      const hostId = String(receivedInvite.from);

      socket.emit("gameAccept", { to: recipientId, gameName: receivedInvite.gameName });
      set({
        receivedInvite: null,
        activeGame: {
          name: receivedInvite.gameName,
          status: 'active',
          hostId: hostId,
          guestId: myId,
          state: getInitialGameState(receivedInvite.gameName, hostId)
        }
      });
    }
  },

  declineGameInvite: () => {
    const { receivedInvite, selectedUser } = get();
    const socket = useAuthStore.getState().socket;
    if (receivedInvite && selectedUser && socket) {
      const recipientId = String(selectedUser._id || selectedUser.id);
      socket.emit("gameDecline", { to: recipientId });
    }
    set({ receivedInvite: null });
  },

  sendGameMove: (moveData) => {
    const { selectedUser, activeGame } = get();
    const socket = useAuthStore.getState().socket;
    if (selectedUser && activeGame && socket) {
      const recipientId = String(selectedUser._id || selectedUser.id);
      socket.emit("gameMove", { to: recipientId, move: moveData });
      set({
        activeGame: {
          ...activeGame,
          state: moveData
        }
      });
    }
  },

  quitGame: () => {
    const { selectedUser, activeGame } = get();
    const socket = useAuthStore.getState().socket;
    if (selectedUser && activeGame && socket) {
      const recipientId = String(selectedUser._id || selectedUser.id);
      socket.emit("gameQuit", { to: recipientId });
    }
    set({ activeGame: null, receivedInvite: null });
  },

  initializeChatStore: () => {
    const socket = useAuthStore.getState().socket;
    if (!socket) return;

    socket.off("gameInvite");
    socket.off("gameAccept");
    socket.off("gameDecline");
    socket.off("gameMove");
    socket.off("gameQuit");

    socket.on("gameInvite", ({ from, gameName, senderName }) => {
      const fromId = String(from);
      set({ receivedInvite: { from: fromId, gameName, senderName } });
      
      const { selectedUser } = get();
      const currentSelectedId = selectedUser ? String(selectedUser._id || selectedUser.id) : null;

      if (currentSelectedId && currentSelectedId.toLowerCase() === fromId.toLowerCase()) {
        toast(`Incoming Game Invite for ${gameName}!`, { icon: '🎮' });
      } else {
        useAuthStore.getState().addNotification({
          id: Date.now(),
          message: `${senderName} invited you to play ${gameName === 'tictactoe' ? 'Tic-Tac-Toe' : gameName === 'truthordare' ? 'Truth or Dare' : 'Relationship Trivia'}!`,
          onClick: () => {
            localStorage.setItem('lastMessageFrom', fromId);
            localStorage.setItem('pendingGameInvite', JSON.stringify({ from: fromId, gameName, senderName }));
            window.location.href = `/`;
          }
        });
        toast(`🎮 New Game Invite from ${senderName}!`, { duration: 5000 });
      }
    });

    socket.on("gameAccept", ({ from, gameName }) => {
      const { selectedUser } = get();
      const authUser = useAuthStore.getState().authUser;
      const myId = String(authUser._id || authUser.id);
      const guestId = String(from);
      const currentSelectedId = selectedUser ? String(selectedUser._id || selectedUser.id) : null;

      if (currentSelectedId && currentSelectedId.toLowerCase() === guestId.toLowerCase()) {
        toast.success("Game invite accepted! Let's play!");
        set({
          receivedInvite: null,
          activeGame: {
            name: gameName,
            status: 'active',
            hostId: myId,
            guestId: guestId,
            state: getInitialGameState(gameName, myId)
          }
        });
      }
    });

    socket.on("gameDecline", ({ from }) => {
      const { selectedUser } = get();
      const currentSelectedId = selectedUser ? String(selectedUser._id || selectedUser.id) : null;
      if (currentSelectedId && currentSelectedId.toLowerCase() === String(from).toLowerCase()) {
        toast.error("Game invite declined.");
        set({ receivedInvite: null });
      }
    });

    socket.on("gameMove", ({ from, move }) => {
      const { selectedUser, activeGame } = get();
      const currentSelectedId = selectedUser ? String(selectedUser._id || selectedUser.id) : null;
      if (currentSelectedId && currentSelectedId.toLowerCase() === String(from).toLowerCase() && activeGame) {
        if (move && move.type === 'draw') {
          const canvas = document.getElementById("codraw-canvas");
          if (canvas) {
            const ctx = canvas.getContext("2d");
            ctx.beginPath();
            ctx.moveTo(move.x0, move.y0);
            ctx.lineTo(move.x1, move.y1);
            ctx.strokeStyle = move.color;
            ctx.lineWidth = 3;
            ctx.lineCap = 'round';
            ctx.stroke();
          }
        } else if (move && move.type === 'canvasClear') {
          const canvas = document.getElementById("codraw-canvas");
          if (canvas) {
            const ctx = canvas.getContext("2d");
            ctx.clearRect(0, 0, canvas.width, canvas.height);
          }
        } else {
          set({
            activeGame: {
              ...activeGame,
              state: move
            }
          });
        }
      }
    });

    socket.on("gameQuit", ({ from }) => {
      const { selectedUser } = get();
      const currentSelectedId = selectedUser ? String(selectedUser._id || selectedUser.id) : null;
      if (currentSelectedId && currentSelectedId.toLowerCase() === String(from).toLowerCase()) {
        toast("Partner left the game.");
        set({ activeGame: null, receivedInvite: null });
      }
    });
  },
}));
